/**
 * Object Manager Hooks
 *
 * Provides unified access to Object Manager data (objects, fields, layouts)
 * Makes Object Manager the source of truth for dynamic UI rendering.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useObjects } from '../services/queries/objectQueries';
import { useFieldsByObject, getPicklistValuesAsStrings as picklistAsStrings, type CustomField } from '../services/queries/fieldQueries';
import type { ListViewColumn } from '../types/listView';
import { persistedPageLayouts, type PageLayout } from '../services/mockData/objectManagerLayouts';
import { useApplicationContext } from '../contexts/ApplicationContext';
import { useRecordPageAssignment } from './useRecordPages';
import { useTabConfigurations } from '../services/queries/tabConfigQueries';
import type { Application } from '../contexts/ApplicationContext';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { formatEpochOrDateAsUsDate, formatEpochOrDateAsUsDateTime } from '../utils/epochDateDisplay';
import { fetchPageLayoutTemplates } from '../services/gobodo/gobodoPageLayoutService';
import type { PageLayoutTemplate } from '../services/api/uiConfigurationApi';
import {
  User,
  FileText,
  Clock,
  CheckSquare,
  Package,
  Mail,
  Paperclip,
  History,
  Briefcase,
  Link,
  Calendar,
} from 'lucide-react';

const APPLICATION_TO_TAB_CONFIG: Partial<Record<Application, string>> = {
  CyberSweep: 'cybersweep',
  CreditRepair: 'credit-repair',
  CreditDevelopment: 'credit-development',
  CreditBlock: 'credit-block',
  IDTheftRecovery: 'id-theft-recovery',
  DebtSettlement: 'debt-settlement',
  RegalMain: 'regal-main',
};

const TAB_ID_TO_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  overview: User,
  details: FileText,
  activity: Clock,
  tasks: CheckSquare,
  products: Package,
  emails: Mail,
  notes: FileText,
  files: Paperclip,
  history: History,
  cases: Briefcase,
  related: Link,
  tab_001: User,
  tab_002: Link,
  tab_003: Clock,
  tab_004: FileText,
};

// ============================================
// OBJECT DEFINITION HOOKS
// ============================================

/**
 * Get object definition from Object Manager
 *
 * @param objectApiName - The API name of the object (e.g., "Lead__c", "Client__c")
 * @returns Object definition with metadata
 *
 * @example
 * const { data: object, isLoading } = useObjectDefinition('Lead__c');
 * console.log(object.label); // "Lead"
 */
export function useObjectDefinition(objectApiName: string) {
  const { data: objects = [], ...rest } = useObjects();

  return {
    ...rest,
    data: objects.find(obj => obj.apiName === objectApiName)
  };
}

/**
 * Get all fields for an object from Object Manager
 *
 * @param objectApiName - The API name of the object
 * @returns Array of field definitions sorted by display order
 *
 * @example
 * const { data: fields = [] } = useObjectFields('obj_client');
 * fields.forEach(field => console.log(field.fieldLabel));
 */
export function useObjectFields(objectId: string) {
  return useFieldsByObject(objectId, { includeInactive: false });
}

/**
 * Get all active fields (including inactive if specified)
 */
export function useAllObjectFields(objectId: string, includeInactive = false) {
  return useFieldsByObject(objectId, { includeInactive });
}

// ============================================
// FILTERED FIELD HOOKS
// ============================================

/**
 * Get visible fields for detail pages
 * Filters to only fields that should appear on detail pages
 *
 * @param objectId - The object ID
 * @returns Array of fields visible on detail pages
 *
 * @example
 * const { data: fields = [] } = useDetailPageFields('obj_lead');
 * // Returns only fields where isActive = true
 */
export function useDetailPageFields(objectId: string) {
  const { data: allFields = [], ...rest } = useObjectFields(objectId);

  // Filter to only visible fields
  // Later: Add role-based filtering based on current user's field-level security
  const visibleFields = allFields.filter(field =>
    field.isActive !== false
  );

  return {
    ...rest,
    data: visibleFields
  };
}

/**
 * Get editable fields for forms
 * Filters to only fields that can be edited
 *
 * @param objectId - The object ID
 * @returns Array of editable fields
 *
 * @example
 * const { data: fields = [] } = useFormFields('obj_client');
 * // Use these fields to build dynamic forms
 */
export function useFormFields(objectId: string) {
  const { data: allFields = [], ...rest } = useObjectFields(objectId);

  // Filter to editable fields only
  // Exclude calculated fields (formula, rollup)
  // Later: Add role-based editing permissions
  const editableFields = allFields.filter(field =>
    field.isActive !== false &&
    field.fieldType !== 'formula' &&
    field.fieldType !== 'rollup' &&
    field.fieldType !== 'autonumber'
  );

  return {
    ...rest,
    data: editableFields
  };
}

/**
 * Convert CustomField from Object Manager to ListViewColumn format
 * Handles type mapping and adds list view specific properties
 */
export function convertFieldToListViewColumn(field: CustomField, index: number): ListViewColumn {
  // Map field types - some fields display as 'badge' or 'boolean' in list views
  let listViewType: ListViewColumn['fieldType'];

  // Fields that should display as badges in list views (status-like fields)
  const badgeFields = ['status', 'stage', 'priority', 'rating', 'type', 'category'];
  const isBadgeField = badgeFields.some(badgeName =>
    field.fieldName.toLowerCase().includes(badgeName)
  );

  if (field.fieldType === 'picklist' && isBadgeField) {
    listViewType = 'badge';
  } else if (field.fieldType === 'multipicklist') {
    listViewType = 'multipicklist';
  } else if (field.fieldType === 'picklist') {
    listViewType = 'picklist';
  } else if (field.fieldType === 'checkbox') {
    // Boolean/checkbox fields display as 'boolean' in list views
    listViewType = 'boolean';
  } else if (field.fieldType === 'richtextarea') {
    // Rich text area fields should render HTML
    listViewType = 'richtextarea';
  } else if (field.fieldType === 'lookup' || field.fieldType === 'masterdetail') {
    // Lookup/masterdetail fields display as 'lookup' in list views
    listViewType = field.fieldType;
  } else if (['text', 'email', 'phone', 'datetime', 'date', 'number'].includes(field.fieldType)) {
    // Direct mapping for compatible types
    listViewType = field.fieldType as 'text' | 'email' | 'phone' | 'datetime' | 'date' | 'number';
  } else {
    // Default to 'text' for all other field types (textarea, etc.)
    listViewType = 'text';
  }

  return {
    fieldApiName: field.fieldName,
    fieldLabel: field.fieldLabel,
    fieldType: listViewType,
    isVisible: true, // Default all fields to visible; can be customized per list view
    order: index,
    lookupObject: field.lookupObject // Include lookup object for relationship fields
  };
}

/**
 * Get list view columns for an object (dynamically from Object Manager)
 * Returns fields formatted as list view columns
 *
 * @param objectApiName - The object API name (e.g., "Lead", "Client")
 * @returns Query result with list view columns
 *
 * @example
 * const { data: columns = [] } = useListViewColumns('Lead');
 * // Returns columns with field metadata for list view rendering
 */
export function useListViewColumns(objectApiName: string) {
  // Map object API names to object IDs for Object Manager
  const objectIdMap: Record<string, string> = {
    'Lead': 'obj_lead',
    'Client': 'obj_client',
    'Contact': 'obj_contact',
    'Opportunity': 'obj_opportunity',
    'Case': 'obj_case',
    'Event': 'obj_event',
    'Task': 'obj_task',
    'Report': 'obj_report',
    'User': 'obj_user',
    // CyberSweep objects
    'CSAudit': 'obj_cs_audit',
    'PAWRecord': 'obj_paw_record'
  };

  const objectId = objectIdMap[objectApiName] || objectApiName;

  // Fetch fields from Object Manager
  const { data: fields = [], ...rest } = useFieldsByObject(objectId, { includeInactive: false });

  // Convert CustomField[] to ListViewColumn[]
  const columns = fields.map((field, index) => convertFieldToListViewColumn(field, index));

  return {
    ...rest,
    data: columns
  };
}

// ============================================
// PAGE LAYOUT HOOKS
// ============================================

/**
 * Get page layout for detail pages
 * Now uses Record Pages assignment system to determine which layout to show
 * based on application context and record data
 *
 * @param objectApiName - The object API name
 * @param record - Optional record data for condition-based assignment
 * @param layoutName - Optional specific layout name (overrides Record Pages)
 * @returns Page layout configuration
 *
 * @example
 * const { data: layout } = usePageLayout('Lead', leadRecord);
 * // Returns layout assigned by Record Pages for current application
 */
export function usePageLayout(objectApiName: string, record?: any, layoutName?: string) {
  const { currentApplication } = useApplicationContext();

  const { data: recordPageAssignment, isFetched: assignmentFetched } = useRecordPageAssignment(objectApiName, {
    application: currentApplication,
    record
  });

  return useQuery({
    queryKey: ['pageLayout', objectApiName, currentApplication, recordPageAssignment?.pageLayoutId, layoutName],
    queryFn: async () => {
      // --- Gobodo path: try fetching layout from backend first ---
      if (USE_GOBODO_API) {
        try {
          const { data: templates } = await fetchPageLayoutTemplates({ objectTypeKey: objectApiName });
          if (templates && templates.length > 0) {
            const chosen = (layoutName && templates.find(t => t.name === layoutName))
              || (recordPageAssignment?.pageLayoutId && templates.find(t => t.id === recordPageAssignment.pageLayoutId))
              || templates.find(t => t.isDefault)
              || templates[0];
            const gobodoLayout = convertGobodoTemplateToPageLayout(chosen, objectApiName);
            // Only use Gobodo layout if it actually has fields; otherwise fall through to mock
            const totalFields = gobodoLayout.sections?.reduce((n, s) => n + (s.fields?.length || 0), 0) || 0;
            if (totalFields > 0) {
              return gobodoLayout;
            }
          }
        } catch {
          // Gobodo layout fetch failed; fall through to mock
        }
      }

      // --- Mock / localStorage path ---
      const layouts = persistedPageLayouts.get();
      const objectLayouts = layouts.filter(
        (layout: PageLayout) => layout.objectApiName === objectApiName
      );

      if (objectLayouts.length === 0) {
        return {
          id: 'layout_default',
          name: 'Default Layout',
          objectApiName,
          sections: [{ id: 'section_default', title: 'Information', columns: 2 as 1 | 2, fields: [] as string[], order: 1 }]
        };
      }

      if (layoutName) {
        const layout = objectLayouts.find((l: PageLayout) => l.name === layoutName);
        if (layout) {
          if (layout.sections) layout.sections = [...layout.sections].sort((a, b) => a.order - b.order);
          return layout;
        }
      }

      if (recordPageAssignment?.pageLayoutId) {
        const assignedLayout = objectLayouts.find(
          (l: PageLayout) => l.id === recordPageAssignment.pageLayoutId
        );
        if (assignedLayout) {
          if (assignedLayout.sections) assignedLayout.sections = [...assignedLayout.sections].sort((a, b) => a.order - b.order);
          return assignedLayout;
        }
      }

      const defaultLayout = objectLayouts.find((l: PageLayout) => l.isDefault) || objectLayouts[0];
      if (defaultLayout.sections) defaultLayout.sections = [...defaultLayout.sections].sort((a, b) => a.order - b.order);
      return defaultLayout;
    },
    enabled: assignmentFetched,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Convert a Gobodo PageLayoutTemplate to the simple PageLayout shape used by modals/detail pages.
 */
function convertGobodoTemplateToPageLayout(template: PageLayoutTemplate, objectApiName: string): PageLayout {
  return {
    id: template.id,
    name: template.name,
    objectApiName,
    isDefault: template.isDefault,
    description: template.description,
    sections: (template.sections || []).map(section => ({
      id: section.id,
      title: section.name,
      columns: Math.min(section.columns, 2) as 1 | 2,
      fields: (section.fields || [])
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(f => f.fieldKey || f.fieldId || f.label),
      order: section.order,
      collapsible: section.collapsible,
      defaultCollapsed: section.defaultCollapsed
    }))
  };
}

/**
 * Get fields for a specific section
 * Returns field definitions in the order specified by the section
 *
 * @param objectApiName - The object API name
 * @param fieldApiNames - Array of field API names from the section
 * @returns Array of field definitions
 *
 * @example
 * const fields = useSectionFields('Lead', ['name', 'email', 'phone']);
 * // Returns CustomField[] for those 3 fields
 */
export function useSectionFields(
  objectApiName: string,
  fieldApiNames: string[]
) {
  // Map object API names to object IDs for Object Manager
  // Support both with and without __c suffix
  const objectIdMap: Record<string, string> = {
    'Lead': 'obj_lead',
    'Lead__c': 'obj_lead',
    'Client': 'obj_client',
    'Client__c': 'obj_client',
    'Contact': 'obj_contact',
    'Contact__c': 'obj_contact',
    'Opportunity': 'obj_opportunity',
    'Opportunity__c': 'obj_opportunity',
    'Case': 'obj_case',
    'Case__c': 'obj_case',
    'Event': 'obj_event',
    'Event__c': 'obj_event',
    'Task': 'obj_task',
    'Task__c': 'obj_task',
    'Report': 'obj_report',
    'Report__c': 'obj_report',
    // CyberSweep objects
    'CSAudit': 'obj_cs_audit',
    'CSAudit__c': 'obj_cs_audit',
    'PAWRecord': 'obj_paw_record',
    'PAWRecord__c': 'obj_paw_record'
  };

  const objectId = objectIdMap[objectApiName] || objectApiName;
  const { data: allFields = [] } = useObjectFields(objectId);

  // Debug logging for PAW fields
  if (objectApiName.includes('PAW') || objectId.includes('paw')) {
    console.log('[useSectionFields] PAW Debug:', {
      objectApiName,
      objectId,
      fieldApiNames,
      allFieldsCount: allFields.length,
      allFieldNames: allFields.map(f => f.fieldName).slice(0, 20),
    });
  }

  const findField = (apiName: string): CustomField | undefined => {
    let field = allFields.find(f => f.fieldName === apiName);
    if (field) return field;

    const pascalCase = apiName.charAt(0).toUpperCase() + apiName.slice(1);
    const withSuffix = pascalCase.endsWith('__c') ? pascalCase : `${pascalCase}__c`;
    field = allFields.find(f => f.fieldName === withSuffix);
    if (field) return field;

    const lowerApiName = apiName.toLowerCase();
    field = allFields.find(f => f.fieldName.toLowerCase() === lowerApiName);
    if (field) return field;

    field = allFields.find(f => f.fieldName.toLowerCase().replace('__c', '') === lowerApiName);
    if (field) return field;

    return undefined;
  };

  return fieldApiNames
    .map(apiName => findField(apiName))
    .filter((field): field is CustomField => field !== undefined);
}

/**
 * Get tabs for detail pages from Tab Config, layout, or defaults
 *
 * @param objectApiName - The object API name (e.g. Client__c)
 * @returns Array of tab configurations with id, label, icon (component)
 */
export function usePageLayoutTabs(objectApiName: string) {
  const { currentApplication } = useApplicationContext();
  const { data: layout } = usePageLayout(objectApiName);
  const appFilter = currentApplication === 'Global' ? undefined : APPLICATION_TO_TAB_CONFIG[currentApplication];
  const { data: tabConfigs } = useTabConfigurations(objectApiName, { application: appFilter });

  const config =
    currentApplication === 'Global'
      ? tabConfigs?.find(c => !c.application || c.application === 'Global' || c.application === 'all')
      : tabConfigs?.[0];
  if (config?.tabs?.length) {
    const visible = config.tabs
      .filter(t => t.isVisible)
      .sort((a, b) => a.order - b.order)
      .map(t => ({
        id: t.id,
        label: t.label,
        icon: TAB_ID_TO_ICON[t.id] ?? TAB_ID_TO_ICON[t.id.toLowerCase()] ?? FileText,
      }));
    if (visible.length) return visible;
  }

  const anyLayout = layout as any;
  if (anyLayout?.tabs?.length) {
    return anyLayout.tabs.map((t: { id: string; label: string; icon?: string }) => ({
      id: t.id,
      label: t.label,
      icon: (typeof t.icon === 'string' ? TAB_ID_TO_ICON[t.icon] : t.icon) ?? FileText,
    }));
  }

  const defaultTabs: Record<string, Array<{ id: string; label: string; icon: React.ComponentType<{ className?: string }> }>> = {
    Lead: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'activity', label: 'Activity', icon: Calendar },
      { id: 'notes', label: 'Notes', icon: FileText },
    ],
    Lead__c: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'activity', label: 'Activity', icon: Calendar },
      { id: 'notes', label: 'Notes', icon: FileText },
    ],
    Client: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      { id: 'products', label: 'Products', icon: Package },
      { id: 'emails', label: 'Emails', icon: Mail },
      { id: 'notes', label: 'Notes', icon: FileText },
      { id: 'files', label: 'Files', icon: Paperclip },
      { id: 'history', label: 'History', icon: History },
    ],
    Client__c: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      { id: 'products', label: 'Products', icon: Package },
      { id: 'emails', label: 'Emails', icon: Mail },
      { id: 'notes', label: 'Notes', icon: FileText },
      { id: 'files', label: 'Files', icon: Paperclip },
      { id: 'history', label: 'History', icon: History },
    ],
    Contact: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'activity', label: 'Activity', icon: Calendar },
      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      { id: 'products', label: 'Products', icon: Package },
      { id: 'files', label: 'Files', icon: Paperclip },
      { id: 'history', label: 'History', icon: History },
    ],
    Contact__c: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'activity', label: 'Activity', icon: Calendar },
      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      { id: 'products', label: 'Products', icon: Package },
      { id: 'files', label: 'Files', icon: Paperclip },
      { id: 'history', label: 'History', icon: History },
    ],
    CSAudit: [
      { id: 'related', label: 'Related', icon: Link },
      { id: 'details', label: 'Details', icon: FileText },
    ],
    CSAudit__c: [
      { id: 'related', label: 'Related', icon: Link },
      { id: 'details', label: 'Details', icon: FileText },
    ],
    PAWRecord: [
      { id: 'related', label: 'Related', icon: Link },
      { id: 'details', label: 'Details', icon: FileText },
    ],
    PAWRecord__c: [
      { id: 'related', label: 'Related', icon: Link },
      { id: 'details', label: 'Details', icon: FileText },
    ],
    Opportunity__c: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'pipeline', label: 'Pipeline', icon: Link },
      { id: 'activity', label: 'Activity', icon: Calendar },
    ],
    Case__c: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'activity', label: 'Activity', icon: Clock },
      { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      { id: 'products', label: 'Products', icon: Package },
      { id: 'files', label: 'Files', icon: Paperclip },
      { id: 'history', label: 'History', icon: History },
    ],
    Event__c: [
      { id: 'overview', label: 'Overview', icon: User },
      { id: 'attendees', label: 'Attendees', icon: User },
      { id: 'notes', label: 'Notes', icon: FileText },
      { id: 'files', label: 'Files', icon: Paperclip },
      { id: 'history', label: 'History', icon: History },
    ],
    Task__c: [
      { id: 'overview', label: 'Overview', icon: CheckSquare },
      { id: 'related', label: 'Related', icon: Link },
      { id: 'subtasks', label: 'Subtasks', icon: CheckSquare },
      { id: 'notes', label: 'Notes', icon: FileText },
      { id: 'history', label: 'History', icon: History },
    ],
    Report__c: [
      { id: 'overview', label: 'Overview', icon: FileText },
      { id: 'results', label: 'Results', icon: FileText },
      { id: 'history', label: 'History', icon: History },
    ],
  };

  return defaultTabs[objectApiName] ?? [{ id: 'overview', label: 'Overview', icon: User }];
}

// ============================================
// FIELD VALUE FORMATTING
// ============================================

/**
 * Format field value based on field type
 * Handles currency, dates, phone numbers, percentages, etc.
 *
 * @param value - The raw field value
 * @param fieldType - The field type from Object Manager
 * @returns Formatted string for display
 *
 * @example
 * formatFieldValue(1500000, 'currency') // "$1,500,000.00"
 * formatFieldValue('2025-01-15', 'date') // "1/15/2025"
 * formatFieldValue(true, 'checkbox') // "✓"
 */
export function formatFieldValue(value: any, fieldType: string): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  switch (fieldType) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(Number(value));

    case 'percent':
      return `${value}%`;

    case 'date':
      return formatEpochOrDateAsUsDate(value);

    case 'datetime':
      return formatEpochOrDateAsUsDateTime(value);

    case 'phone':
      // Format phone number (handles 10-digit US format)
      const cleaned = String(value).replace(/\D/g, '');
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      return String(value);

    case 'email':
      return String(value);

    case 'url':
      return String(value);

    case 'checkbox':
    case 'boolean':
      return value ? '✓' : '✗';

    case 'number':
      return new Intl.NumberFormat('en-US').format(Number(value));

    case 'picklist':
    case 'multipicklist':
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return String(value);

    default:
      return String(value);
  }
}

/**
 * Get field by API name from list of fields
 * Helper function to find a specific field
 *
 * @param fields - Array of fields
 * @param fieldApiName - The field API name to find
 * @returns The field definition or undefined
 */
export function getFieldByApiName(fields: CustomField[], fieldApiName: string): CustomField | undefined {
  return fields.find(f => f.fieldName === fieldApiName);
}

/**
 * Get field type for a specific field API name
 * Convenience function to quickly get a field's type
 *
 * @param fields - Array of fields
 * @param fieldApiName - The field API name
 * @returns The field type or 'text' as default
 */
export function getFieldType(fields: CustomField[], fieldApiName: string): string {
  const field = getFieldByApiName(fields, fieldApiName);
  return field?.fieldType || 'text';
}

/**
 * Check if a field is required
 *
 * @param fields - Array of fields
 * @param fieldApiName - The field API name
 * @returns True if field is required
 */
export function isFieldRequired(fields: CustomField[], fieldApiName: string): boolean {
  const field = getFieldByApiName(fields, fieldApiName);
  return field?.required || false;
}

/**
 * Get picklist values for a field
 *
 * @param fields - Array of fields
 * @param fieldApiName - The field API name
 * @returns Array of picklist values or empty array
 */
export function getPicklistValues(fields: CustomField[], fieldApiName: string): string[] {
  const field = getFieldByApiName(fields, fieldApiName);
  // PE-1470: Use helper to handle both string[] and PicklistValue[] formats
  return picklistAsStrings(field?.picklistValues);
}
